// src/pages/News.jsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Search, Zap } from 'lucide-react';
import { useSupabase } from '../contexts/SupabaseContext';
import { format } from 'date-fns';

const News = () => {
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { supabase } = useSupabase();

  useEffect(() => {
    fetchArticles();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = articles.filter(article =>
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredArticles(filtered);
    } else {
      setFilteredArticles(articles);
    }
  }, [searchTerm, articles]);

  const fetchArticles = async () => {
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('published', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching articles:', error);
      } else {
        setArticles(data || []);
        setFilteredArticles(data || []);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const ArticleCard = ({ article }) => (
    <Link to={`/read/${article.id}`}>
      <div className="group h-full bg-white rounded-2xl shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 overflow-hidden border border-gray-100 flex flex-col">
        {/* Image Container */}
        {article.image_url && (
          <div className="relative h-56 overflow-hidden bg-gray-100">
            <img
              src={article.image_url}
              alt={article.title}
              className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
            />
          </div>
        )}
        
        {/* Content */}
        <div className="p-6 flex flex-col flex-1">
          <div className="flex items-center gap-2 text-xs text-red-600 font-black uppercase tracking-widest mb-3">
            <Calendar className="w-4 h-4" />
            {format(new Date(article.created_at), 'MMM d, yyyy')}
          </div>
          
          <h3 className="text-xl font-black text-gray-900 mb-3 line-clamp-2 group-hover:text-red-600 transition-colors leading-tight">
            {article.title}
          </h3>
          
          <p className="text-gray-600 text-sm line-clamp-3 mb-6 flex-1 leading-relaxed">
            {article.excerpt}
          </p>
          
          <div className="flex items-center gap-2 text-red-600 font-black text-xs uppercase tracking-wide group-hover:gap-3 transition-all">
            Read More <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </Link>
  );

  // Skeleton Loader
  const NewsSkeleton = () => (
    <div className="animate-pulse min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="h-12 w-48 bg-gray-300 rounded mx-auto mb-4"></div>
          <div className="h-6 w-96 bg-gray-300 rounded mx-auto"></div>
        </div>
        <div className="max-w-md mx-auto mb-12">
          <div className="h-12 bg-gray-300 rounded-xl"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white rounded-2xl overflow-hidden">
              <div className="h-56 bg-gray-300"></div>
              <div className="p-6 space-y-4">
                <div className="h-4 w-24 bg-gray-300 rounded"></div>
                <div className="h-6 w-full bg-gray-300 rounded"></div>
                <div className="h-16 w-full bg-gray-300 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (loading) return <NewsSkeleton />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white font-sans text-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-6 bg-red-600/10 text-red-600 px-4 py-2 rounded-full text-sm font-black">
            <Zap className="w-4 h-4" />
            Latest Updates
          </div>
          <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-4 leading-tight">
            Automotive News
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Stay up to date with the latest reveals, industry developments, and automotive stories from around the world
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-16">
          <div className="relative group">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 group-focus-within:text-red-600 transition-colors" />
            <input
              type="text"
              placeholder="Search articles by title or keyword..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-6 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all text-base"
            />
          </div>
        </div>

        {/* Results Count */}
        {filteredArticles.length > 0 && (
          <div className="mb-8 text-sm font-semibold text-gray-600">
            Showing <span className="text-red-600 font-black">{filteredArticles.length}</span> {filteredArticles.length === 1 ? 'article' : 'articles'}
          </div>
        )}

        {/* Articles Grid */}
        {filteredArticles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="py-20">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-2xl mb-6">
                <Zap className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-2xl font-black text-gray-900 mb-2">
                {searchTerm ? 'No articles found' : 'No articles yet'}
              </h3>
              <p className="text-gray-600 max-w-md mx-auto mb-8">
                {searchTerm
                  ? 'Try adjusting your search terms or browse all articles'
                  : 'Check back later for the latest automotive news and updates'}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-black transition-all transform hover:scale-105"
                >
                  Clear Search <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bottom CTA */}
      <div className="relative py-20 overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-black mt-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-0 w-96 h-96 bg-red-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-700 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
            Stay Informed with Our <span className="text-red-500">News Updates</span>
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
            Get the latest automotive news, industry insights, and breaking stories delivered to your inbox.
          </p>
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-xl font-black transition-all transform hover:scale-105 shadow-lg shadow-red-900/30"
          >
            Back to Home <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default News;